import { CloseOutlined } from '@ant-design/icons';
import {
  MyLeadsFriendlyName,
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
} from '@mm/state/interfaces/myLeads';
import { Tag, Tooltip } from 'antd';
import { uniqueId } from 'lodash';
import React from 'react';
import {
  mapFilterParamsToTags,
  mapQueryToTags,
} from '../MyLeadsFiltersPanel/helpers';
import { useFilterParams } from '../MyLeadsFiltersPanel/use-filter-params';
import styles from './MyLeadsFilterBar.module.css';
import classNames from 'classnames';
import { selectAudienceVariables } from '@mm/state/selectors/audienceSelector';
import { useSelector } from 'react-redux';

export type Tag = {
  key: string;
  subKey?: string;
  value: string | boolean;
  friendlyName: string;
  lowercase?: boolean;
  fatTag?: string;
  type: MyLeadsFilterTypes;
};

export const MyLeadsFilterBar = ({
  filterParams,
  setFilterParams,
}: {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
}) => {
  const { hasFilters, getUpdatedFilterParams } = useFilterParams({
    filterParams,
  });
  const audienceVariables = useSelector(selectAudienceVariables);

  const mappedAttributesToTags = mapQueryToTags(
    filterParams.query?.attributes,
    MyLeadsFilterTypes.attributes,
  );
  const mappedVariablesToTags = mapQueryToTags(
    filterParams.query?.variables,
    MyLeadsFilterTypes.variables,
  );
  const mappedExcludedVariabledToTags = mapQueryToTags(
    filterParams.query?.excludedVariables,
    MyLeadsFilterTypes.excludedVariables,
  );
  const mappedFilterParamsToTags = mapFilterParamsToTags(
    filterParams.filterParams,
    audienceVariables,
  );

  const tags = [
    ...mappedAttributesToTags,
    ...mappedVariablesToTags,
    ...mappedExcludedVariabledToTags,
    ...mappedFilterParamsToTags,
  ].filter(Boolean);

  const onTagRemove = (tag: Tag) => {
    const updatedParams = getUpdatedFilterParams({
      key: tag.key,
      value: tag.value,
      isExisting: true,
      type: tag.type,
    });

    if (tag.subKey) {
      delete updatedParams.query?.attributes?.created_at?.[0][tag.subKey];
    }

    setFilterParams(updatedParams);
  };

  const onResetAll = () => {
    setFilterParams({ query: {} });
  };

  if (!hasFilters) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {Boolean(hasFilters) && (
          <Tag
            color="default"
            closable
            closeIcon={<CloseOutlined className={styles.closeIcon} />}
            onClose={onResetAll}
            onClick={onResetAll}
            className={styles.resetAll}
          >
            Reset All
          </Tag>
        )}
        {tags?.map((tag: Tag) => {
          const key = `${uniqueId(tag.friendlyName)}`;
          return (
            <Tooltip
              title={MyLeadsFriendlyName[tag.key]}
              key={key}
              overlayClassName={styles.tooltip}
            >
              <Tag
                key={key}
                color="geekblue"
                className={classNames(styles.tag, {
                  [styles.lowercase]: tag?.lowercase,
                })}
                closable
                onClose={() => onTagRemove(tag)}
              >
                {tag?.fatTag && (
                  <span className={styles.fatTag}>{tag.fatTag}:</span>
                )}

                {tag.friendlyName}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
